<template>
  <header class="w-full bg-white shadow-sm px-6 py-3 relative">
    <nav aria-label="Primary" class="flex justify-between items-center lg:max-w-[1300px] mx-4 lg:mx-auto">
      <!-- Brand -->
      <a
        href="/"
        class="flex items-center gap-2"
        :aria-label="`${appName} home`"
        data-testid="navbar-logo"
        @click.prevent="router.push('/')"
      >
        <img v-if="!isWhiteLabel" :src="brandedLogo" :alt="`${appName} home`" class="h-12 md:h-20 w-auto max-w-[180px] md:max-w-[350px] object-contain" />
        <img v-else-if="schoolLogo" :src="schoolLogo" :alt="`${appName} home`" class="h-12 md:h-20 w-auto max-w-[180px] md:max-w-[350px] object-contain" />
        <span v-else class="text-xl md:text-2xl font-bold text-[#1A56DB]">{{ appName }}</span>
      </a>

      <!-- Right side (Desktop) -->
      <ul class="hidden md:flex items-center gap-8 text-gray-700 font-medium list-none m-0 p-0">
        <li v-for="item in landingNavItems" :key="item.key">
          <router-link
            :to="item.to"
            class="hover:text-[#1A56DB] transition"
            :exact-active-class="item.active ? landingNavActiveClass : ''"
            :data-testid="`navbar-${item.key}-link`"
          >{{ item.label }}</router-link>
        </li>
        <li v-if="!isWhiteLabel">
          <CountryPicker />
        </li>
        <li>
          <Button
            label="Sign In"
            icon="pi pi-sign-in"
            class="!bg-[#1A56DB] hover:!bg-[#2563EB] !border-[#1A56DB] !text-white text-base font-semibold px-7 py-3 rounded-md shadow-sm transition"
            data-testid="navbar-signin-btn"
            @click="handleButtonClick"
          />
        </li>
      </ul>

      <!-- Hamburger Menu (mobile) -->
      <button
        type="button"
        :aria-expanded="menuOpen"
        aria-controls="mobile-menu"
        :aria-label="menuOpen ? 'Close menu' : 'Open menu'"
        class="md:hidden text-gray-700 focus:outline-none z-50"
        data-testid="navbar-menu-toggle"
        @click="toggleMenu"
      >
        <i class="pi text-2xl" :class="menuOpen ? 'pi-times' : 'pi-bars'" aria-hidden="true"></i>
      </button>
    </nav>

    <!-- Overlay -->
    <transition name="fade">
      <div
        v-if="menuOpen"
        class="fixed inset-0 bg-black/10 bg-opacity-40 z-40"
        aria-hidden="true"
        data-testid="navbar-menu-overlay"
        @click="toggleMenu"
      ></div>
    </transition>

    <!-- Sidebar menu -->
    <transition name="slide">
      <nav
        v-if="menuOpen"
        id="mobile-menu"
        aria-label="Mobile primary"
        class="fixed top-0 right-0 h-full w-64 bg-white shadow-lg z-50 flex flex-col items-start px-6 py-8 gap-6 text-gray-700 font-medium"
      >
        <router-link
          v-for="item in landingNavItems"
          :key="item.key"
          :to="item.to"
          class="hover:text-[#1A56DB] transition"
          :exact-active-class="item.active ? landingNavActiveClass : ''"
          :data-testid="`navbar-mobile-${item.key}-link`"
          @click="toggleMenu"
        >{{ item.label }}</router-link>

        <CountryPicker v-if="!isWhiteLabel" class="w-full" />

        <Button
          label="Sign In"
          icon="pi pi-sign-in"
          class="!bg-[#1A56DB] hover:!bg-[#2563EB] !border-[#1A56DB] !text-white w-full mt-4"
          data-testid="navbar-mobile-signin-btn"
          @click="handleButtonClick"
        />
      </nav>
    </transition>
  </header>
</template>

<script setup lang="ts">
import { useBranding } from '@/composables/useBranding';
import CountryPicker from '@/components/landing/CountryPicker.vue';
import brandedLogo from '@/assets/svg/branded-logo.png';
import { landingNavItems, landingNavActiveClass } from '@/constants/general-constants';

const menuOpen = ref(false);
const toggleMenu = () => (menuOpen.value = !menuOpen.value);

const { appName, isWhiteLabel, schoolLogo } = useBranding();
const router = useRouter();

const handleButtonClick = () => {
  router.push('/login');
};
</script>

<style scoped>
/* Fade animation for overlay */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Slide-in animation for sidebar */
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}
.slide-enter-from {
  transform: translateX(100%);
}
.slide-leave-to {
  transform: translateX(100%);
}
</style>
